import { mergeRefs } from '@ipfabric/frontend-shared-assets/utils/react/mergeRefs';
import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import type { Point } from '@ipfabric/utils';
import type { FC } from 'react';
import { useContextMenuPosition } from 'utils/hooks/useContextMenuPosition';
import { DropdownMenu, type DropdownMenuProps } from './DropdownMenu';

interface UseContextMenuProps {
  container: HTMLElement | null;
  onClose: () => void;
  position: Point;
}

const useContextMenu = (props: UseContextMenuProps) => {
  const menuRef = useClickAway<HTMLDivElement>(() => {
    props.onClose();
  });

  const { position, refCallback } = useContextMenuPosition(props);
  return {
    menuRef: mergeRefs(menuRef, refCallback),
    position,
  };
};

export interface ContextMenuProps extends DropdownMenuProps {
  /**
   * Pass in a container element to ensure the context menu will be visible
   *
   * When you open the context menu too close to right or bottom of that container,
   * it will do the best effort to reposition to other side to be visible.
   */
  container?: HTMLElement | null;
  /**
   * Close callback that will be triggered after item click or click outside context menu
   *
   * Made mandatory to not forget to provide it to make the context menu handle this properly.
   */
  onClose: () => void;
  /**
   * Set position of the context menu, likely based on mouse click position
   */
  position: Point;
}

export const ContextMenu: FC<ContextMenuProps> = (props) => {
  const { menuRef, position } = useContextMenu({
    container: props.container ?? null,
    onClose: props.onClose,
    position: props.position,
  });

  return (
    <DropdownMenu
      disableContextMenu
      items={props.items}
      style={{
        left: position.x,
        top: position.y,
      }}
      ref={menuRef}
      onClose={props.onClose}
    />
  );
};
